import {
  DIFFICULTIES,
  type Difficulty,
  RACE_DURATION_OPTIONS,
  RACE_FORMATS,
  RACE_LEVEL_OPTIONS,
  type RaceFormat,
  type RaceSettings,
} from '@cube-rush/shared';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { Button } from '../components/Button';
import { Segmented, type SegmentedOption } from '../components/controls';
import { Dialog } from '../components/Dialog';
import { durationText } from '../race/raceSummary';
import './RaceSettingsDialog.css';

const FORMAT_OPTIONS: ReadonlyArray<SegmentedOption<RaceFormat>> = RACE_FORMATS.map((value) => ({
  value,
  label: ru.race.formats[value],
  icon: ru.race.formatIcons[value],
}));

const DIFFICULTY_OPTIONS: ReadonlyArray<SegmentedOption<Difficulty>> = DIFFICULTIES.map(
  (value) => ({ value, label: ru.race.difficulties[value], icon: ru.race.difficultyIcons[value] }),
);

/**
 * The standard choices plus the current value when it is none of them: a server set up with
 * other defaults (tests: 2 levels, 50 seconds) shows its length as one more chip.
 */
function withCurrent(standard: readonly number[], current: number): number[] {
  return standard.includes(current) ? [...standard] : [...standard, current].sort((a, b) => a - b);
}

/**
 * The race the room runs: by levels or against the clock, how long, how hard. The host changes
 * it between races, every tap at once (the dialog shows the room's settings as the server has
 * them); everybody else, and everybody while a race runs, sees the same dialog read-only.
 */
export function RaceSettingsDialog({ onClose }: { onClose(): void }) {
  const app = useApp();
  const room = app.room.room.value;
  const isHost = app.room.isHost.value;
  if (!room) return null;

  const running = room.phase === 'countdown' || room.phase === 'race';
  // A running race shows what it runs with; between races, what the next one will be.
  const settings: RaceSettings = (running ? room.race?.settings : null) ?? room.settings;
  const editable = isHost && !running;
  const host = room.members.find((member) => member.id === room.hostId);
  const note = editable
    ? null
    : isHost
      ? ru.race.lockedNote
      : host
        ? ru.race.hostNote(host.profile.name)
        : null;
  const change = (patch: Partial<RaceSettings>) => {
    if (editable) app.changeRaceSettings(patch);
  };

  const levelOptions = withCurrent(RACE_LEVEL_OPTIONS, settings.levelCount).map((value) => ({
    value,
    label: String(value),
  }));
  const timeOptions = withCurrent(RACE_DURATION_OPTIONS, settings.durationSec).map((value) => ({
    value,
    label: durationText(value, true),
  }));
  const byLevels = settings.format === 'levels';

  return (
    <Dialog title={ru.race.title} onClose={onClose} wide>
      <div class="race-dialog">
        {note && <p class="race-dialog__note">{note}</p>}
        <div class="race-dialog__column">
          <div class="race-dialog__group">
            <div class="race-dialog__label">{ru.race.formatLabel}</div>
            <Segmented
              label={ru.race.formatLabel}
              options={FORMAT_OPTIONS}
              value={settings.format}
              disabled={!editable}
              onChange={(format) => change({ format })}
            />
            <p class="race-dialog__hint">{ru.race.formatHints[settings.format]}</p>
          </div>
          <div class="race-dialog__group">
            <div class="race-dialog__label">
              {byLevels ? ru.race.levelsLabel : ru.race.timeLabel}
            </div>
            {byLevels ? (
              <Segmented
                key="levels"
                label={ru.race.levelsLabel}
                options={levelOptions}
                value={settings.levelCount}
                disabled={!editable}
                onChange={(levelCount) => change({ levelCount })}
              />
            ) : (
              <Segmented
                key="time"
                label={ru.race.timeLabel}
                options={timeOptions}
                value={settings.durationSec}
                disabled={!editable}
                onChange={(durationSec) => change({ durationSec })}
              />
            )}
          </div>
        </div>
        <div class="race-dialog__column">
          <div class="race-dialog__group race-dialog__group--difficulty">
            <div class="race-dialog__label">{ru.race.difficultyLabel}</div>
            <Segmented
              label={ru.race.difficultyLabel}
              options={DIFFICULTY_OPTIONS}
              value={settings.difficulty}
              disabled={!editable}
              grid
              onChange={(difficulty) => change({ difficulty })}
            />
            <p class="race-dialog__hint">{ru.race.difficultyHints[settings.difficulty]}</p>
          </div>
          <Button variant="green" block class="race-dialog__done" onClick={onClose}>
            {ru.common.done}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
